import { z } from 'zod';

// --- Webhook provider/receiver (Zod-first) ---

export const WebhookEventDescriptorSchema = z.object({
  source: z.string(),
  event: z.string(),
  description: z.string().optional(),
});
export type WebhookEventDescriptor = z.infer<typeof WebhookEventDescriptorSchema>;

/**
 * Derives a WebhookEventDescriptor[] from an event schema map.
 * Webhook providers should define a `{ [eventName]: ZodSchema }` map as
 * their source of truth, then call this to generate the events array
 * for `webhookProvider.events`.
 *
 * @example
 * const MY_EVENT_SCHEMAS = { push: PushSchema, pull_request: PRSchema };
 * webhookProvider: {
 *   source: 'github',
 *   events: buildWebhookEvents('github', MY_EVENT_SCHEMAS),
 *   route: '/webhooks/github',
 * }
 */
export function buildWebhookEvents<T extends Record<string, unknown>>(
  source: string,
  schemaMap: T,
  descriptions?: Partial<Record<keyof T, string>>,
): WebhookEventDescriptor[] {
  return Object.keys(schemaMap).map((event) => ({
    source,
    event,
    ...(descriptions?.[event as keyof T] ? { description: descriptions[event as keyof T] } : {}),
  }));
}

export const WebhookProviderDefinitionSchema = z.object({
  source: z.string(),
  events: z.array(WebhookEventDescriptorSchema),
  route: z.string(),
});
export type WebhookProviderDefinition = z.infer<typeof WebhookProviderDefinitionSchema>;

/**
 * Canonical in-process event delivered through the host
 * {@link WebhookEventDeliveryPort}. Webhook ingress is one producer; any
 * plugin or internal source can build one of these and emit it via
 * `ctx.services.events.emit(event)`.
 *
 * `raw` is optional because it is webhook-specific (the original parsed JSON
 * payload sent by the external provider). Non-webhook producers omit it.
 */
export const ReflexEventSchema = z.object({
  source: z.string(),
  event: z.string(),
  action: z.string().optional(),
  payload: z.unknown(),
  raw: z.record(z.string(), z.unknown()).optional(),
  /**
   * Active organization id derived from the install / source resource the
   * event arrived for. Always required: producers must resolve a concrete
   * org id (falling back to a `'system'` sentinel for global / env-bootstrap
   * installs) before publishing the event.
   */
  organizationId: z.string(),
  /**
   * Optional idempotency key carried from the provider's native delivery id
   * (GitHub `x-github-delivery`, Slack `event_id`, Linear
   * `webhookId`+`webhookTimestamp`, ...). Behavior-neutral for the simple
   * in-process impl, which ignores it. A future durable
   * {@link WebhookEventDeliveryPort} uses it to dedupe redeliveries. Producers
   * populate it at ingress where the provider supplies one.
   */
  deliveryId: z.string().optional(),
  /**
   * Marks a derived/secondary event (e.g. a flow trigger emitted alongside
   * a primary agent dispatch) whose receivers must never block the
   * producer's provider ack. Emitters that ack an external caller (the
   * inline webhook sink, Slack Socket Mode dispatch) kick these off
   * without awaiting receiver completion; receiver failures are still
   * logged by the delivery port. Mode-agnostic: it describes the event,
   * not the delivery mode, and ack-decoupled consumers may ignore it.
   */
  fireAndForget: z.boolean().optional(),
});
export type ReflexEvent<T = unknown> = Omit<z.infer<typeof ReflexEventSchema>, 'payload'> & {
  payload: T;
};

export type EventReceiverHandler<T = unknown> = (event: ReflexEvent<T>) => void | Promise<void>;

/**
 * Subscribes to events from a specific source. Used by plugins that react to
 * external or internal events.
 *
 * `source: '*'` subscribes to every source, and `'*'` inside `events`
 * matches every event name — together they form a wildcard receiver (used
 * by plugin-flows to match trigger nodes against ANY bus event).
 */
export interface EventReceiverDefinition<T = unknown> {
  source: string;
  events: string[];
  handler: EventReceiverHandler<T>;
  /**
   * Optional stable identifier for this receiver. Behavior-neutral for the
   * simple in-process impl. A future durable {@link WebhookEventDeliveryPort}
   * uses it to track per-receiver delivery (retry / DLQ) across restarts, so
   * it should be unique and stable across deploys when set.
   */
  name?: string;
}

/**
 * Creates a typed receiver factory for a webhook (or other event) provider.
 * The factory returns an `EventReceiverDefinition` with the payload
 * automatically typed from the provider's event schema map.
 *
 * @example
 * // In plugin-github/src/index.ts:
 * export const githubReceiver = buildReceiverFactory('github', GITHUB_EVENT_SCHEMAS);
 *
 * // In plugin-leaderboard/src/index.ts:
 * githubReceiver('pull_request', (event) => {
 *   event.payload.pull_request.html_url; // fully typed
 * })
 */
export function buildReceiverFactory<T extends Record<string, z.ZodType>>(
  source: string,
  _schemaMap: T,
) {
  return <K extends string & keyof T>(
    event: K,
    handler: (event: ReflexEvent<z.infer<T[K]>>) => void | Promise<void>,
  ): EventReceiverDefinition => ({
    source,
    events: [event],
    handler: handler as EventReceiverHandler,
  });
}

// --- Dispatch helper ---

/**
 * Dispatches an event to all matching receivers (by source + event type).
 * Fire-and-forget with per-receiver error isolation. Used by the simple
 * in-process {@link WebhookEventDeliveryPort} and available to any caller that
 * already holds a receiver list.
 *
 * Returns the number of receivers the event matched so the caller can report
 * it (e.g. as {@link EmitResult.matched}).
 */
export function dispatchEvent(
  receivers: EventReceiverDefinition[],
  event: ReflexEvent,
  log: { error: (obj: unknown, msg?: string) => void },
): number {
  const matching = matchingEventReceivers(receivers, event);
  for (const receiver of matching) {
    Promise.resolve(receiver.handler(event)).catch((err) => {
      log.error({ err, source: event.source, event: event.event }, 'Event receiver error');
    });
  }
  return matching.length;
}

/** Shared source/event matching rule used by inline and durable delivery. */
export function matchingEventReceivers(
  receivers: readonly EventReceiverDefinition[],
  event: ReflexEvent,
): EventReceiverDefinition[] {
  return receivers.filter(
    (r) =>
      (r.source === '*' || r.source === event.source) &&
      (r.events.includes('*') || r.events.includes(event.event)),
  );
}

// --- Back-compat aliases (deprecated) ---
//
// The original webhook-only names. New code should prefer `ReflexEvent`,
// `EventReceiverDefinition`, `EventReceiverHandler`, and `dispatchEvent`.
// These aliases keep the ~190 historical call sites compiling while they
// migrate.

/** @deprecated Use {@link ReflexEventSchema} instead. */
export const WebhookEventSchema = ReflexEventSchema;
/** @deprecated Use {@link ReflexEvent} instead. */
export type WebhookEvent<T = unknown> = ReflexEvent<T>;
/** @deprecated Use {@link EventReceiverHandler} instead. */
export type WebhookReceiverHandler<T = unknown> = EventReceiverHandler<T>;
/** @deprecated Use {@link EventReceiverDefinition} instead. */
export type WebhookReceiverDefinition<T = unknown> = EventReceiverDefinition<T>;
/** @deprecated Use {@link dispatchEvent} instead. */
export const dispatchToReceivers = dispatchEvent;
